// src/pages/Tour/TeamTours.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import tourService from '../../services/tour.service';
import Toast from '../../components/Toast/Toast';
import './TeamTours.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];

const formatDate = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const formatTime = (d) => d
  ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
  : '—';

const TOUR_TYPE_COLORS = {
  WORKING:     { bg: '#e8f5e9', color: '#2e7d32' },
  LEAVE:       { bg: '#fff8e1', color: '#f57f17' },
  CAMPAIGNING: { bg: '#e3f2fd', color: '#1565c0' },
};

const STATUS_COLORS = {
  IN_PROGRESS: { bg: '#e3f2fd', color: '#1565c0' },
  COMPLETED:   { bg: '#e8f5e9', color: '#2e7d32' },
};

const TRANSPORT_ICONS = {
  BIKE:             '🏍️',
  CAR:              '🚗',
  TAXI:             '🚕',
  PUBLIC_TRANSPORT: '🚌',
};

const VISIT_TYPE_COLORS = {
  FIRST_VISIT: { bg: '#e8f5e9', color: '#2e7d32' },
  FOLLOWUP:    { bg: '#e3f2fd', color: '#1565c0' },
};

// ── Visit Details Drawer ──────────────────────────────────────────────────────
const VisitDrawer = ({ tour, onClose }) => {
  const [visits,  setVisits]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await tourService.getTourVisits(tour.id);
        if (res.success) setVisits(res.data || []);
      } catch {
        setVisits([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tour.id]);

  return (
    <div className="tt-drawer-overlay" onClick={onClose}>
      <div className="tt-drawer" onClick={e => e.stopPropagation()}>
        {/* Drawer header */}
        <div className="tt-drawer-header">
          <div>
            <h5 className="tt-drawer-title">
              {tour.firstName} {tour.lastName}
              <span className="tt-role-chip">{tour.roleDisplayName}</span>
            </h5>
            <p className="tt-drawer-subtitle">
              {formatDate(tour.tourDate)} · {tour.tourType} · {TRANSPORT_ICONS[tour.modeOfTransport] || ''} {tour.modeOfTransport?.replace('_', ' ') || '—'}
            </p>
          </div>
          <button className="tt-drawer-close" onClick={onClose}>✕</button>
        </div>

        {/* KM summary */}
        <div className="tt-km-row">
          <div className="tt-km-box">
            <p className="tt-km-label">Start KM</p>
            <p className="tt-km-val">{tour.startKm ?? '—'}</p>
          </div>
          <div className="tt-km-divider">→</div>
          <div className="tt-km-box">
            <p className="tt-km-label">End KM</p>
            <p className="tt-km-val">{tour.endKm ?? '—'}</p>
          </div>
          <div className="tt-km-divider">=</div>
          <div className="tt-km-box" style={{ background: '#e8f5e9' }}>
            <p className="tt-km-label">Distance</p>
            <p className="tt-km-val" style={{ color: '#2e7d32' }}>
              {tour.startKm != null && tour.endKm != null
                ? `${tour.endKm - tour.startKm} km`
                : '—'}
            </p>
          </div>
        </div>

        {/* Farmer visits */}
        <div className="tt-drawer-body">
          <p className="tt-section-label">
            Farmer Visits
            <span className="tt-count-chip">{tour.visitCount}</span>
          </p>

          {loading ? (
            <div className="tt-loading-sm">Loading visits...</div>
          ) : visits.length === 0 ? (
            <div className="tt-empty-sm">No farmer visits logged for this tour</div>
          ) : (
            <div className="tt-visit-list">
              {visits.map((v, idx) => (
                <div key={v.id} className="tt-visit-card">
                  <div className="tt-visit-top">
                    <span
                      className="tt-visit-type"
                      style={{
                        background: VISIT_TYPE_COLORS[v.visitType]?.bg,
                        color:      VISIT_TYPE_COLORS[v.visitType]?.color,
                      }}
                    >
                      {v.visitType === 'FIRST_VISIT' ? 'First Visit' : 'Followup'}
                    </span>
                    <span className="tt-visit-time">{formatTime(v.visitTime)}</span>
                  </div>
                  <p className="tt-visit-farmer">{v.farmerName}</p>
                  <p className="tt-visit-meta">
                    🏘️ {v.village}
                    {v.crop && <> · 🌾 {v.crop}</>}
                    {v.acre && <> · {v.acre}</>}
                  </p>
                  {v.reason && <p className="tt-visit-reason"><strong>Reason:</strong> {v.reason}</p>}
                  {v.remark && <p className="tt-visit-reason"><strong>Remark:</strong> {v.remark}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const TeamTours = () => {
  const { user } = useAuth();

  const [tours,      setTours]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState(null);
  const [selected,   setSelected]   = useState(null); // tour for drawer
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({
    fromDate: today(),
    toDate:   today(),
  });

  const fetchTours = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tourService.getTeamTours(
        filters.fromDate,
        filters.toDate,
        pagination.page,
        pagination.limit
      );
      if (res.success) {
        setTours(res.data.tours || []);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      }
    } catch {
      setToast({ message: 'Failed to fetch team tours', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  useEffect(() => { fetchTours(); }, [filters, pagination.page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const setQuickDate = (type) => {
    const now = new Date();
    if (type === 'today') {
      const d = today();
      setFilters({ fromDate: d, toDate: d });
    } else if (type === 'week') {
      const from = new Date(now);
      from.setDate(now.getDate() - 6);
      setFilters({
        fromDate: from.toISOString().split('T')[0],
        toDate:   today(),
      });
    } else if (type === 'month') {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      setFilters({
        fromDate: from.toISOString().split('T')[0],
        toDate:   today(),
      });
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Export CSV
  const handleExport = () => {
    if (tours.length === 0) return;
    const headers = ['Name', 'Role', 'Date', 'Tour Type', 'Status', 'Start KM', 'End KM', 'Distance', 'Transport', 'Destination', 'Visits'];
    const rows = tours.map(t => [
      `${t.firstName} ${t.lastName}`,
      t.roleDisplayName,
      formatDate(t.tourDate),
      t.tourType,
      t.status,
      t.startKm ?? '',
      t.endKm ?? '',
      t.startKm != null && t.endKm != null ? t.endKm - t.startKm : '',
      t.modeOfTransport ?? '',
      t.startDestination ?? '',
      t.visitCount,
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `team-tours-${filters.fromDate}-to-${filters.toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Summary stats
  const working     = tours.filter(t => t.tourType === 'WORKING').length;
  const leave       = tours.filter(t => t.tourType === 'LEAVE').length;
  const totalVisits = tours.reduce((sum, t) => sum + (t.visitCount || 0), 0);
  const completed   = tours.filter(t => t.status === 'COMPLETED').length;

  return (
    <div className="tt-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Breadcrumb */}
      <nav className="tt-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span><span>Tour</span>
        <span>›</span><span>Team Tours</span>
      </nav>

      {/* Header */}
      <div className="tt-header">
        <div>
          <h4 className="tt-title">Team Tours</h4>
          <p className="tt-subtitle">
            Tour activity of your team — filtered by your hierarchy
          </p>
        </div>
        <button className="tt-export-btn" onClick={handleExport} disabled={tours.length === 0}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="tt-filters">
        <div className="tt-date-row">
          <div className="tt-filter-group">
            <label className="tt-filter-label">From Date</label>
            <input
              type="date"
              name="fromDate"
              className="tt-date-input"
              value={filters.fromDate}
              onChange={handleFilterChange}
              max={today()}
            />
          </div>
          <div className="tt-filter-group">
            <label className="tt-filter-label">To Date</label>
            <input
              type="date"
              name="toDate"
              className="tt-date-input"
              value={filters.toDate}
              onChange={handleFilterChange}
              max={today()}
            />
          </div>
          <div className="tt-quick-dates">
            <button className="tt-quick-btn" onClick={() => setQuickDate('today')}>Today</button>
            <button className="tt-quick-btn" onClick={() => setQuickDate('week')}>This Week</button>
            <button className="tt-quick-btn" onClick={() => setQuickDate('month')}>This Month</button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="tt-summary">
        {[
          { label: 'Total Tours',    value: pagination.total, color: '#1565c0', bg: '#e3f2fd' },
          { label: 'Working',        value: working,          color: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Leave',          value: leave,            color: '#f57f17', bg: '#fff8e1' },
          { label: 'Completed',      value: completed,        color: '#1b5e20', bg: '#c8e6c9' },
          { label: 'Farmer Visits',  value: totalVisits,      color: '#6a1b9a', bg: '#f3e5f5' },
        ].map(c => (
          <div key={c.label} className="tt-summary-card" style={{ background: c.bg, borderLeft: `4px solid ${c.color}` }}>
            <p className="tt-summary-count" style={{ color: c.color }}>{c.value}</p>
            <p className="tt-summary-label">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="tt-table-wrap">
        {loading ? (
          <div className="tt-loading">Loading team tours...</div>
        ) : tours.length === 0 ? (
          <div className="tt-empty">
            <p style={{ fontSize: 32 }}>🗺️</p>
            <p>No tours found for the selected date range</p>
          </div>
        ) : (
          <table className="tt-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Role</th>
                <th>Date</th>
                <th>Tour Type</th>
                <th>Status</th>
                <th>Transport</th>
                <th>Start KM</th>
                <th>End KM</th>
                <th>Distance</th>
                <th>Visits</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {tours.map(tour => {
                const typeStyle   = TOUR_TYPE_COLORS[tour.tourType]   || {};
                const statusStyle = STATUS_COLORS[tour.status]         || {};
                const distance    = tour.startKm != null && tour.endKm != null
                  ? `${tour.endKm - tour.startKm} km` : '—';

                return (
                  <tr key={tour.id}>
                    <td>
                      <div className="tt-agent-cell">
                        <div className="tt-avatar">
                          {tour.firstName?.[0]}{tour.lastName?.[0]}
                        </div>
                        <div>
                          <p className="tt-agent-name">{tour.firstName} {tour.lastName}</p>
                          <p className="tt-agent-code">{tour.employeeCode}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="tt-role-pill">{tour.roleDisplayName}</span>
                    </td>
                    <td style={{ fontSize: 13 }}>{formatDate(tour.tourDate)}</td>
                    <td>
                      <span className="tt-badge" style={{ background: typeStyle.bg, color: typeStyle.color }}>
                        {tour.tourType}
                      </span>
                    </td>
                    <td>
                      <span className="tt-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                        {tour.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {TRANSPORT_ICONS[tour.modeOfTransport] || ''} {tour.modeOfTransport?.replace('_', ' ') || '—'}
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>{tour.startKm ?? '—'}</td>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>{tour.endKm   ?? '—'}</td>
                    <td style={{ fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>{distance}</td>
                    <td>
                      <span className="tt-visit-count-badge">
                        {tour.visitCount} visit{tour.visitCount !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      <button
                        className="tt-detail-btn"
                        onClick={() => setSelected(tour)}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="tt-pagination">
          <button
            className="tt-page-btn"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            ← Previous
          </button>
          <span className="tt-page-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button
            className="tt-page-btn"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next →
          </button>
        </div>
      )}

      {/* Visit Drawer */}
      {selected && (
        <VisitDrawer tour={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

export default TeamTours;